"use client";
import { useGlobalContent } from "@app/[locale]/GlobalContentProvider";
import { RichText } from "@components/cms/rich-text-renderer/RichText";
import { Banner } from "@components/commerce/categories/banner";
import { CategoryNavigation } from "@components/ui/category-navigation/CategoryNavigation";
import type { ILink, IPageProductCategoryPage } from "@contentful-api/types/contentful";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { ModulesRenderer } from "@lib/contentful/modules-renderer/ModulesRenderer";
import { PageHeading } from "@ui/components/content/page-heading/PageHeading";
import { Box } from "@ui/components/layout/box/Box";
import { MainWrapper } from "@components/ui/main-wrapper/mainWrapper";

type ProductCategoryPageProps = {
    page: IPageProductCategoryPage;
};

export const ProductCategoryPage = ({ page }: ProductCategoryPageProps) => {
    const { siteSettings } = useGlobalContent();
    const authorizationInvitation = siteSettings?.fields?.authorizationInvitation;

    const fields = page?.fields;
    const {
        title,
        desc,
        titleSize = "Extra Large",
        descriptionSize = "Normal",
        modules,
        subNavigationItems,
    } = fields ?? {};

    const itemsWithLinkImages = (subNavigationItems?.fields?.links || []).filter(
        (item) => (item as ILink)?.fields?.linkImage?.fields?.file?.url
    );

    const { getMultiple } = useMicrocopy();

    const microcopies = getMultiple(CommonCMS.shared, [
        CommonCMS.generic.readMore,
        CommonCMS.generic.readLess,
    ]);

    return (
        <>
            <Box mt={[5, 8]}>
                <PageHeading
                    title={title}
                    description={<RichText data={desc} />}
                    showMoreLabel={microcopies[CommonCMS.generic.readMore]}
                    showLessLabel={microcopies[CommonCMS.generic.readLess]}
                    titleSize={titleSize}
                    descriptionSize={descriptionSize}
                />
            </Box>
            <MainWrapper>
                {itemsWithLinkImages?.length === 0 ? (
                    <CategoryNavigation items={subNavigationItems} />
                ) : (
                    <Box ml={[3, 3, 7, 6]}>
                        <Banner bannerItems={subNavigationItems?.fields?.links} />
                    </Box>
                )}
                <ModulesRenderer
                    modules={modules}
                    authorizationInvitation={authorizationInvitation}
                />
            </MainWrapper>
        </>
    );
};
